/** Exponat-Sprites aus public/exhibits/exhibits.json (optional). */
export type ExhibitMetaEntry = { file: string; w: number; h: number };
type ExhibitsJson = { tile: number; exhibits: Record<string, ExhibitMetaEntry> };

let tile = 64;
let entries: Record<string, ExhibitMetaEntry> = {};
const images = new Map<string, HTMLImageElement>();
let loadPromise: Promise<void> | null = null;
let metaLoaded = false;

export function loadExhibits(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const res = await fetch(import.meta.env.BASE_URL + 'exhibits/exhibits.json');
      if (!res.ok) return;
      const j = (await res.json()) as ExhibitsJson;
      tile = j.tile || 64;
      entries = j.exhibits || {};
      metaLoaded = true;
      await Promise.all(
        Object.entries(entries).map(
          ([key, e]) =>
            new Promise<void>((resolve) => {
              const img = new Image();
              img.onload = () => {
                images.set(key, img);
                resolve();
              };
              img.onerror = () => resolve();
              img.src = import.meta.env.BASE_URL + 'exhibits/' + e.file;
            }),
        ),
      );
    } catch {
      /* Platzhalter */
    }
  })();
  return loadPromise;
}

/** Größe in Welt-Einheiten (tile * Kacheln) oder null. */
export function exhibitSize(key: string | undefined): { w: number; h: number } | null {
  if (!key) return null;
  const e = entries[key];
  if (!e) return null;
  return { w: e.w * tile, h: e.h * tile };
}

export function exhibitImage(key: string | undefined): HTMLImageElement | null {
  if (!key) return null;
  const img = images.get(key);
  return img && img.complete && img.naturalWidth > 0 ? img : null;
}

export function exhibitStatus(): { meta: boolean; loaded: number; expected: number } {
  return { meta: metaLoaded, loaded: images.size, expected: Object.keys(entries).length };
}

/** Stand-Bodenbilder aus public/stands/<id>.webp (optional, Fallback = Farbfläche). */
const standImages = new Map<string, HTMLImageElement>();
const standRequested = new Set<string>();
export function loadStandImages(ids: string[]): void {
  for (const id of ids) {
    if (standRequested.has(id)) continue;
    standRequested.add(id);
    const img = new Image();
    img.onload = () => standImages.set(id, img);
    img.src = import.meta.env.BASE_URL + 'stands/' + id + '.webp';
  }
}
export function standImage(id: string | undefined): HTMLImageElement | null {
  if (!id) return null;
  const img = standImages.get(id);
  return img && img.complete && img.naturalWidth > 0 ? img : null;
}
export function standImageStatus(): { loaded: number; requested: number } {
  return { loaded: standImages.size, requested: standRequested.size };
}

/** Beliebige Ortsbilder (Pfad relativ zu public/, aus Datendatei). */
const placeImages = new Map<string, HTMLImageElement>();
export function loadPlaceImage(path: string): void {
  if (placeImages.has(path)) return;
  const img = new Image();
  img.src = import.meta.env.BASE_URL + path;
  placeImages.set(path, img);
}
export function placeImage(path: string): HTMLImageElement | null {
  const img = placeImages.get(path);
  return img && img.complete && img.naturalWidth > 0 ? img : null;
}
